import { Modal, Stack } from "@mantine/core"
import { useForm, UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconCheck, IconX } from "@tabler/icons-react"
import { useMemo, useState, useRef, useEffect } from "react"
import { add, RuleGroupType } from "react-querybuilder"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import {
  DigestRequests,
  DigestResponses,
  DigestType,
} from "@costory/types/endpoints/digest"
import { defaultSearchEvents, Filters } from "@costory/types/filters"
import {
  AggBy,
  Currency,
  DatePreset,
  ExplorerChartType,
} from "@costory/types/prisma-client"

import dayjs from "@costory/shared/dayjs"
import { OTHERS } from "@costory/shared/utils/explorer_data"
import { computePresetDates } from "@costory/shared/utils/filters"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { AdvancedExplorer } from "@costory/front/pages/DigestV2/components/AdvancedExplorer"
import { DigestHeader } from "@costory/front/pages/DigestV2/components/DigestHeader"
import { DigestModalContextEditor } from "@costory/front/pages/DigestV2/components/DigestModalContextEditor"
import { PageType } from "@costory/front/pages/DigestV2/components/types"
import {
  buildNodePath,
  getEndOfMonthPlusDelay,
  getOptions,
  getReactQueryBuilderQuery,
  NodeTree,
  replaceDigestItem,
} from "@costory/front/pages/DigestV2/helper"
import { useAxesMapperQuery } from "@costory/front/queries/axes"
import { useAddExplorerImageToNote } from "@costory/front/queries/digest"

type Props = {
  period: DigestRequests.Period
  selectedNodeDrawer: NodeTree
  isEditMode: boolean
}

type PropsPrefillExplorer = {
  form: UseFormReturnType<Filters>
  data: NodeTree["data"]
  digestForm: UseFormReturnType<DigestType>
  page: PageType
  setPage: (page: PageType) => void
  selectedNodeDrawer: NodeTree
  setManuallyCollapsedRows: (
    rows: string[] | ((prev: string[]) => string[]),
  ) => void
  period: DigestRequests.Period
  exclusionWhereClause: RuleGroupType
  groupByKey: string
  initialValues: Filters
  nodesMap: Map<string, NodeTree>
  costColumn: string
  loading: boolean
  addExplorerImageToNote: ReturnType<
    typeof useAddExplorerImageToNote
  >["mutateAsync"]
  editContent: string
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  currentNodeFilters: Filters
  setLoading?: (loading: boolean) => void
}

const PrefillExplorerUI = ({
  form,
  data,
  digestForm,
  page,
  setPage,
  selectedNodeDrawer,
  initialValues,
  chartType,
  setChartType,
  costColumn,
  loading,
  addExplorerImageToNote,
  editContent,
  setEditContent,
  exclusionWhereClause,
  currentNodeFilters,
  setLoading,
}: PropsPrefillExplorer & {
  chartType: ExplorerChartType
  setChartType: (type: ExplorerChartType) => void
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null!)
  const [hideComparisonChart, setHideComparisonChart] = useState(false)
  const [showComparisonChart, { toggle: toggleShowComparisonChart }] =
    useDisclosure(false)
  // Find the node in the form by id
  const [, setSearchParams] = useSearchParams()
  const handleSaveView = async () => {
    setPage(PageType.Default)
    setSearchParams({ mode: "preview" })

    // Include the current chartType in the filters to ensure correct chart type is generated
    const filtersWithChartType = {
      ...form.values,
      explorerChartType: chartType,
    }

    await addExplorerImageToNote({
      filters: filtersWithChartType,
      showComparisonChart: !hideComparisonChart && showComparisonChart,
      setEditContent,
    })
    form.setFieldValue("bookmarkedEvents", undefined)
  }
  // Others Node with no hidden items
  const isNotOtherNode =
    !selectedNodeDrawer.isOthers ||
    !!(selectedNodeDrawer.nodeProps?.hiddenItems?.length ?? 0)
  switch (page) {
    case PageType.AdvancedExplorer:
      return (
        <AdvancedExplorer
          chartType={chartType}
          setChartType={setChartType}
          form={form}
          handleSaveView={handleSaveView}
          isNotOtherNode={isNotOtherNode}
          loading={loading}
          showComparisonChart={showComparisonChart}
          toggleShowComparisonChart={toggleShowComparisonChart}
          setHideComparisonChart={setHideComparisonChart}
        />
      )
    default:
      return (
        <DigestModalContextEditor
          initialFormValues={initialValues}
          data={data}
          textareaRef={textareaRef}
          close={close}
          loading={loading}
          editContent={editContent}
          setEditContent={setEditContent}
          selectedNodeDrawer={selectedNodeDrawer}
          digestForm={digestForm}
          form={form}
          setChartType={setChartType}
          setPage={setPage}
          costColumn={costColumn}
          exclusionWhereClause={exclusionWhereClause}
          currentNodeFilters={currentNodeFilters}
          isNotOtherNode={isNotOtherNode}
          setLoading={setLoading}
        />
      )
  }
}
// Helper to map ExplorerChartType to ChartType for Chart component
export const ExplorerModal = ({
  selectedNodeDrawer,
  period,
  digestForm,
  setManuallyCollapsedRows,
  costColumn,
  nodesMap,
  exclusionWhereClause,
  digest,
  isEditMode,
}: Props & {
  digestForm: UseFormReturnType<DigestType>
  setManuallyCollapsedRows: React.Dispatch<React.SetStateAction<string[]>>
  costColumn: string
  nodesMap: Map<string, NodeTree>
  exclusionWhereClause: RuleGroupType
  digest: DigestResponses.Investigation
}) => {
  const {
    nodeProps: { position },
    data,
  } = selectedNodeDrawer
  const {
    mutateAsync: addExplorerImageToNote,
    isPending: addingExplorerImageToNote,
  } = useAddExplorerImageToNote()
  const { selectedNodeId } = useParams()
  const queryMapper = useAxesMapperQuery()

  const convertDateFields = (filters: Filters): Filters => ({
    ...filters,
    from: filters.from,
    to: filters.to,
    previousFrom: filters.previousFrom,
    previousTo: filters.previousTo,
  })

  const updatedFilters =
    selectedNodeDrawer.nodeProps.filtersUrl &&
    convertDateFields(selectedNodeDrawer.nodeProps.filtersUrl)

  // prepare base filters
  const hiddenItems = useMemo(
    () => selectedNodeDrawer.nodeProps.hiddenItems ?? [],
    [selectedNodeDrawer.nodeProps.hiddenItems],
  )

  // Get all siblings (children under the same parent, excluding Others)
  const siblings = digestForm.values.items.filter(
    (item) =>
      item.parentId === selectedNodeDrawer.nodeProps.parentId &&
      item.direction !== OTHERS,
  )
  const childs = digestForm.values.items.filter(
    (item) => item.parentId === selectedNodeDrawer.nodeProps.id,
  )

  const groupByKey = getBestGroupByKey({
    selectedNodeDrawer,
    childs,
    hiddenItems,
    digest,
    costColumn,
  })
  const positionFilters = useMemo(() => {
    return position.map((elem) => getReactQueryBuilderQuery(elem))
  }, [position])
  // Get all visible children values for the groupByKey

  const initialFilterValue = getInitialFilterValue({
    groupByKey,
    positionFilters,
    period,
    isOthers: selectedNodeDrawer.isOthers,
    digestForm,
    costColumn,
    siblings,
    hiddenItems,
    exclusionWhereClause,
  })
  const form = useForm<Filters>({
    initialValues: {
      ...(updatedFilters ?? initialFilterValue),
      eventsFilter: {
        ...defaultSearchEvents,
        enableAllGithubRepos: true,
        commitments: true,
        marketplacePurchases: true,
        reservations: true,
      },
    },
  }) as UseFormReturnType<Filters>

  const [page, setPage] = useState<PageType>(PageType.Default)
  const [chartType, setChartType] = useState<ExplorerChartType>(
    ExplorerChartType.LINE,
  )
  // LIFTED STATE: currentContent for note editing
  // Shared loading state for chart actions
  const [sharedLoading, setSharedLoading] = useState(false)

  const currentItem = digestForm.values.items.find(
    (item) => item.id === selectedNodeDrawer.nodeProps.id,
  )
  const labels = currentItem?.labels || []
  const [editContent, setEditContent] = useState(
    selectedNodeDrawer.nodeProps.note || "",
  )

  useEffect(() => {
    setEditContent(selectedNodeDrawer.nodeProps.note || "")
  }, [selectedNodeDrawer.nodeProps.id, selectedNodeDrawer.nodeProps.note])
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const mode = searchParams.get("mode")
  if (!mode && !!selectedNodeId) {
    setSearchParams({ mode: "edit" })
  }

  // Wrap the close handler to always persist the latest note
  const handleClose = () => {
    replaceDigestItem(
      digestForm,
      {
        note: editContent || "",
        labels: labels.length > 0 ? labels : undefined,
      },
      selectedNodeDrawer.nodeProps.id,
    )
    const nodeIdIndex = isEditMode ? 4 : 5
    navigate(location.pathname.split("/").slice(0, nodeIdIndex).join("/"), {
      replace: true,
    })
    close()
  }

  const costSummary = (
    <QueryWrapper query={queryMapper}>
      {({ data: axes }) => {
        const axesPathArr = nodesMap
          ? buildNodePath(selectedNodeDrawer, nodesMap, axes)
          : []
        const lastAxis =
          axesPathArr.length > 0 ? axesPathArr[axesPathArr.length - 1] : ""
        const fullAxesPath = axesPathArr.join(" → ")
        return (
          <DigestHeader
            data={data}
            period={digestForm.values.period}
            fullAxesPath={fullAxesPath}
            lastAxis={lastAxis}
          />
        )
      }}
    </QueryWrapper>
  )

  return (
    <Modal
      title={costSummary}
      opened={!!selectedNodeId}
      closeButtonProps={{
        icon: page == PageType.AdvancedExplorer ? <IconX /> : <IconCheck />,
      }}
      classNames={{
        content: "custom-modal-content",
      }}
      onClose={() =>
        page == PageType.AdvancedExplorer
          ? setPage(PageType.Default)
          : handleClose()
      }
      closeOnClickOutside={true}
      centered
      size="95%"
      fullScreen={true}
      data-intercom-target="costory-tour-digest-main-modal"
    >
      <Stack gap="md">
        <PrefillExplorerUI
          form={form}
          data={data}
          digestForm={digestForm}
          page={page}
          setPage={setPage}
          selectedNodeDrawer={selectedNodeDrawer}
          setManuallyCollapsedRows={setManuallyCollapsedRows}
          period={period}
          groupByKey={groupByKey}
          initialValues={updatedFilters ?? initialFilterValue}
          chartType={chartType}
          setChartType={setChartType}
          nodesMap={nodesMap}
          loading={addingExplorerImageToNote || sharedLoading}
          costColumn={costColumn}
          addExplorerImageToNote={addExplorerImageToNote}
          editContent={editContent}
          setEditContent={setEditContent}
          exclusionWhereClause={exclusionWhereClause}
          currentNodeFilters={initialFilterValue}
          setLoading={setSharedLoading}
        />
      </Stack>
    </Modal>
  )
}

const getInitialFilterValue = ({
  groupByKey,
  positionFilters,
  period,
  isOthers,
  digestForm,
  costColumn,
  siblings,
  hiddenItems,
  exclusionWhereClause,
}: {
  groupByKey: string
  positionFilters: {
    field: string
    operator: string
    value: string[] | null
  }[]
  period: DigestRequests.Period
  isOthers: boolean
  digestForm: UseFormReturnType<DigestType>
  costColumn: string
  siblings: DigestType["items"]
  hiddenItems: NodeTree["nodeProps"]["hiddenItems"]
  exclusionWhereClause: RuleGroupType
}) => {
  const visibleChildrenValues = siblings
    .map((sib) => sib.position.find((p) => p.key === groupByKey)?.value)
    .filter(Boolean)

  // If there are hidden items, remove their values from visibleChildrenValues
  const hiddenValues = (hiddenItems ?? [])
    .map((item) => item.position.find((p) => p.key === groupByKey)?.value)
    .filter(Boolean)

  const visibleValues = visibleChildrenValues.filter(
    (val) => !hiddenValues.includes(val),
  )

  let whereClause
  if (isOthers) {
    // For Others node, we want to show values that belong to the parent but are not valid children
    whereClause = {
      combinator: "and" as const,
      rules: [
        // First, ensure we only get values that belong to the parent
        ...positionFilters,
        // Then exclude valid children
        {
          field: groupByKey,
          operator: "notIn",
          value: visibleValues,
        },
      ],
    }
  } else {
    whereClause = {
      combinator: "and" as const,
      rules: positionFilters,
    }
  }
  // This is needed to set the datePreset to LAST_MONTH when the period is a only lastmonth
  const isLastMonth = dayjs(digestForm.values.period).isSame(
    dayjs().subtract(1, "month").startOf("month"),
    "day",
  )
  return {
    metricId: costColumn,
    currency: "USD" as Currency,
    limit: 10,
    aggBy: "Day" as AggBy,
    groupBy: groupByKey,
    explorerChartType: ExplorerChartType.LINE,
    ...computePresetDates(
      period === "MonthToMonth"
        ? "LAST_MONTH"
        : period === "MTD"
          ? "MTD"
          : "LAST_3_MONTHS",
      getEndOfMonthPlusDelay(digestForm.values.period),
    ),
    datePreset: isLastMonth ? DatePreset.LAST_MONTH : null,
    whereClause: add(
      { combinator: "and", rules: [whereClause] },
      exclusionWhereClause,
      [],
    ),
    eventsFilter: {
      ...defaultSearchEvents,
      enableAllGithubRepos: true,
      commitments: true,
      marketplacePurchases: true,
      skuPriceChanges: true,
      reservations: true,
    },
    bookmarkedEvents: undefined,
  }
}

const getBestGroupByKey = ({
  selectedNodeDrawer,
  childs,
  hiddenItems,
  digest,
  costColumn,
}: {
  selectedNodeDrawer: NodeTree
  childs: DigestType["items"]
  hiddenItems: NodeTree["nodeProps"]["hiddenItems"]
  digest: DigestResponses.Investigation
  costColumn: string
}) => {
  // If no parent, return default
  if (!selectedNodeDrawer.nodeProps.parentId) {
    return "cos_provider"
  }

  // Algorithm: Best Group BY
  // 1. If a child exists: take the axis of the child
  if (childs.length > 0 && childs.length > 1) {
    return childs.map((e) => e.direction).filter((e) => e !== OTHERS)[0]
  }

  // 2. If no child exists: compute suggested child axis
  const suggestedOptions = getOptions(
    digest,
    selectedNodeDrawer.nodeProps.position,
    costColumn,
  )

  // If there are suggested options, return the best one
  if (suggestedOptions.length > 0) {
    return suggestedOptions[0].direction
  }

  // If no suggestions, fallback to hidden items if available
  if ((hiddenItems ?? []).length > 0) {
    return (hiddenItems ?? [])[0].direction
  }

  // If no hidden items, fallback to the last position key
  if (selectedNodeDrawer.nodeProps.position.length > 0) {
    return selectedNodeDrawer.nodeProps.position[
      selectedNodeDrawer.nodeProps.position.length - 1
    ].key
  }

  // Final fallback
  return "cos_provider"
}
